import {
  FC,
  ReactNode,
  Children,
  isValidElement,
  cloneElement,
  Fragment,
} from "react";
import { cx } from "../cx";

const MENTIONS_REGEXP = /@<([^>]+?)>\[([^\]]+?)\]/g;

interface MentionRendererOptions {
  userId: string;
  username: string;
  isUnstyled?: boolean;
}

// userId might be useful in the future
const mentionRenderer = ({ username, isUnstyled }: MentionRendererOptions) => (
  <span className={cx(!isUnstyled && "mention font-bold text-primary-500")}>
    {username}
  </span>
);

export const mentionifyString = (
  text: string,
  isUnstyled?: boolean,
  renderer: (options: MentionRendererOptions) => ReactNode = mentionRenderer
) => {
  const parts = text.split(MENTIONS_REGEXP);

  return parts.map((part, index) => {
    // check if index matches the first captured group (userId)
    if (index % 3 === 1) {
      const [userId, username] = [parts[index], parts[index + 1]];

      return (
        <Fragment key={+index}>
          {renderer({ userId, username, isUnstyled })}
        </Fragment>
      );
    }

    // skip the second captured group (username) index as it was already
    // processed in the previous iteration
    if (index % 3 === 2) {
      return null;
    }

    return part;
  });
};

export const mentionifyNode = (
  node: ReactNode,
  isUnstyled?: boolean,
  customRenderer?: (options: MentionRendererOptions) => ReactNode
): ReactNode => {
  if (typeof node === "string") {
    return mentionifyString(node, isUnstyled, customRenderer);
  }

  // handle arrays of nodes
  if (Array.isArray(node)) {
    return node.map((child) =>
      mentionifyNode(child, isUnstyled, customRenderer)
    );
  }

  // traverse children of the node recursively
  if (isValidElement<{ children?: ReactNode }>(node)) {
    const nodeChildren = node.props.children
      ? Children.map(node.props.children, (child) =>
          mentionifyNode(child, isUnstyled, customRenderer)
        )
      : node.props.children;

    return cloneElement(node, { key: node.key }, nodeChildren);
  }

  if (node === null || node === undefined) {
    return node;
  }

  return node.toString();
};

export interface MentionifyProps {
  children: ReactNode;
  isUnstyled?: boolean;
  customRenderer?: (options: MentionRendererOptions) => ReactNode;
}

export const Mentionify: FC<MentionifyProps> = ({
  children,
  isUnstyled,
  customRenderer,
}) => (
  <>
    {Children.map(children, (node) =>
      mentionifyNode(node, isUnstyled, customRenderer)
    )}
  </>
);
